import './Landingpage.scss';

import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import { ThemeProvider, createTheme } from '@mui/material';
import { deDE } from '@mui/material/locale';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useRouteMatch } from 'react-router-dom';

import AuthenticationActionCreator from '../../actions/AuthenticationActionCreator';
import isValidEmail from '../../core/typeguards/isValidEmail';
import { FORGOT_PASSWORD_PATH, FORGOT_PASSWORD_PATHH, REGISTER_USER } from '../../models/Paths';
import { IRequestDataFailedAction } from '../../models/state';
import { IUser } from '../../models/user/IUser';
import { getAuthenticationError } from '../../selectors/AuthenticationSelectors';
import FBLoginButton from '../../services/Facebook/FBLoginButton';
import useLoginHandler from '../../services/Facebook/useLoginHandler';
import GoogleLoginButton from '../../services/Google/GoogleLoginButton';
import useGoogleLoginHandler from '../../services/Google/useGoogleLoginHandler';
import NotificationActionCreator from '../../services/Notifications/actions/NotificationActionCreator';
import { DefaultTheme } from '../../theme/DefaultTheme';
import LoginModal from '../../components/NewRegister/Login';
import ForgotPasswordModal from '../../components/ForgotPasswordModal';
import ForgotPasswordModalx from '../../components/ForgotPasswordModalx';
import heroBackground from '../../assets/images/landingpage/bg.jpg';
import fetivoLogo from '../../assets/images/logos/logo-with-writing.svg';

export interface ILandingpageProps {}

const brandName = 'Fetivo.de';

const audienceItems = [
    { label: 'Lesben', className: 'pink' },
    { label: 'Gay', className: 'blue' },
    { label: 'Singles', className: 'violet' },
    { label: 'BDSM / Sado', className: 'red' },
    { label: 'KV', className: 'orange' },
    { label: 'LLL', className: 'rose' },
    { label: 'PT', className: 'indigo' },
    { label: 'TS', className: 'purple' },
    { label: 'TV', className: 'magenta' },
];

const statItems = [
    { value: '257.093', label: 'Mitglieder' },
    { value: '190.348', label: 'Weiblich' },
    { value: '66.745', label: 'Männlich' },
    { value: '54.597', label: 'Online heute' },
];

const featureItems = [
    { icon: 'chat', title: 'Chat & Matching', text: 'Direktnachrichten und spontane Matches.' },
    { icon: 'shield', title: 'Privatsphäre', text: 'Diskrete Nutzung mit Fokus auf Privatsphäre.' },
    { icon: 'fire', title: 'Verifiziert', text: 'Mehr Vertrauen, mehr echte Kontakte.' },
];

export const Landingpage = React.memo((props: ILandingpageProps) => {
    const forgotPasswordRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATH, exact: true });
    const forgotPasswordLegacyRoute = useRouteMatch({ path: FORGOT_PASSWORD_PATHH, exact: true });
    const isForgotPasswordOpen = Boolean(forgotPasswordRoute);
    const isSetPasswordOpen = Boolean(forgotPasswordLegacyRoute);

    const dispatch = useDispatch();
    const history = useHistory();
    const error = useSelector(getAuthenticationError);
    const handleFBLogin = useLoginHandler();
    const handleGoogleLogin = useGoogleLoginHandler();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setLoading] = useState(false);
    const [loginModalOpen, setLoginModalOpen] = useState(false);

    useEffect(() => {
        void (async () => {
            const searchParams = new URLSearchParams(history.location.search);
            try {
                const ext = searchParams.get('ext');
                if (ext) {
                    const json = JSON.parse(atob(ext)) as [IUser];
                    await dispatch(AuthenticationActionCreator.transientLogin(json));
                }

                const redirect = searchParams.get('redirect');
                if (redirect) {
                    history.replace(redirect);
                }
            } catch {
                //
            }
        })();
    }, [dispatch, history]);

    const showError = useCallback(
        (message: string) => {
            dispatch(
                NotificationActionCreator.enqueueSnackbar({
                    key: String(new Date().getTime() + Math.random()),
                    message,
                    options: { variant: 'error' },
                })
            );
        },
        [dispatch]
    );

    const handleRegister = useCallback(() => {
        history.push(REGISTER_USER);
    }, [history]);

    const handleInlineLogin = useCallback(async () => {
        if (!email.trim() || !password.trim()) {
            showError(!email.trim() ? 'Bitte E-Mail eingeben.' : 'Bitte Passwort eingeben.');
            return;
        }

        if (!isValidEmail(email)) {
            showError('Bitte eine gueltige E-Mail eingeben.');
            return;
        }

        setLoading(true);
        (await dispatch(AuthenticationActionCreator.loginUser({ email, password }))) as IRequestDataFailedAction;
        setLoading(false);
        history.push('/');
    }, [dispatch, email, history, password, showError]);

    const handleLoginKeyDown = useCallback(
        async (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === 'Enter') {
                await handleInlineLogin();
            }
        },
        [handleInlineLogin]
    );

    useEffect(() => {
        if (error?.Note === 'Error Password') {
            showError('E-Mail oder Passwort falsch.');
        }
    }, [error, showError]);

    return (
        <ThemeProvider theme={createTheme(DefaultTheme, deDE)}>
            <main className="fetivo-page">
                <header className="fetivo-header">
                    <div className="fetivo-container fetivo-header__inner">
                        <button className="fetivo-brand" type="button" onClick={() => history.push('/')}>
                            <img src={fetivoLogo} alt={brandName} className="fetivo-brand__logo" />
                            <span>Echte Kontakte, Diskret &amp; Privat</span>
                        </button>

                        <div className="fetivo-header-login" aria-label="Login">
                            <input
                                type="email"
                                placeholder="E-Mail"
                                value={email}
                                onChange={(event) => setEmail(event.target.value)}
                                onKeyDown={handleLoginKeyDown}
                                autoComplete="email"
                            />
                            <input
                                type="password"
                                placeholder="Passwort"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                onKeyDown={handleLoginKeyDown}
                                autoComplete="current-password"
                            />
                            <button type="button" onClick={handleInlineLogin} disabled={isLoading}>
                                {isLoading ? '...' : 'Login'}
                            </button>
                        </div>

                        <button className="fetivo-mobile-login" type="button" onClick={() => setLoginModalOpen(true)}>
                            Einloggen
                        </button>
                    </div>
                </header>

                <section className="fetivo-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(0, 0, 0, 0.82), rgba(0, 0, 0, 0.44)))` }}>
                    <div className="fetivo-container fetivo-hero__inner">
                        <div className="fetivo-hero__content">
                            <div className="fetivo-badges">
                                {/*<span className="green">8.499 online gerade</span>*/}
                                <span className="blue">Verifizierte Profile</span>
                                <span className="gold">Testsieger</span>
                            </div>

                            <h1>Echte Kontakte, Diskret &amp; Privat</h1>
                            <p>Bunt gemischte Community f&uuml;r diskrete Kontakte und echte Begegnungen.</p>

                            <div className="fetivo-audience-grid" aria-label="Kategorien">
                                {audienceItems.map((item) => (
                                    <button key={item.label} className={`fetivo-audience-card ${item.className}`} type="button">
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <aside className="fetivo-signup-card" aria-label="Registrierung">
                            <div className="fetivo-trust-list">
                                <div>8.499 Mitglieder online gerade</div>
                                <div>Verifizierte Profile</div>
                                <div>Testsieger Community</div>
                            </div>

                            <button className="fetivo-action-button email" type="button" onClick={handleRegister}>
                                <EmailRoundedIcon fontSize="small" />
                                Weiter mit E-Mail
                            </button>

                            <div className="fetivo-social-button google">
                                <GoogleLoginButton setLoading={setLoading} onLoginFinished={handleGoogleLogin} />
                            </div>

                            <div className="fetivo-social-button facebook">
                                <FBLoginButton text="Mit Facebook anmelden" setLoading={setLoading} onLoginFinished={handleFBLogin} />
                            </div>

                            <button className="fetivo-action-button login" type="button" onClick={() => setLoginModalOpen(true)}>
                                Schon Mitglied? Hier einloggen
                            </button>
                        </aside>
                    </div>
                </section>

                <section className="fetivo-stats fetivo-container" aria-label="Statistiken">
                    {statItems.map((item) => (
                        <div className="fetivo-stat-card" key={item.label}>
                            <strong>{item.value}</strong>
                            <span>{item.label}</span>
                        </div>
                    ))}
                </section>

                <section className="fetivo-features fetivo-container">
                    <h2>Warum Fetivo?</h2>
                    <div className="fetivo-feature-grid">
                        {featureItems.map((item) => (
                            <article className={`fetivo-feature-card ${item.icon}`} key={item.title}>
                                <span className="fetivo-feature-icon" aria-hidden="true" />
                                <h3>{item.title}</h3>
                                <p>{item.text}</p>
                            </article>
                        ))}
                    </div>
                </section>

                <section className="fetivo-verified fetivo-container">
                    <div className="fetivo-checkmark">✓</div>
                    <div>
                        <strong>FETIVO GEPR&Uuml;FT</strong>
                        <span>Diskret &amp; verifizierte Community</span>
                    </div>
                </section>

                <section className="fetivo-final-cta fetivo-container">
                    <button type="button" onClick={handleRegister}>
                        <LoginRoundedIcon fontSize="small" />
                        Jetzt kostenlos registrieren
                    </button>
                </section>

                <footer className="fetivo-footer">
                    <nav className="fetivo-footer__links" aria-label="Rechtliches">
                        <Link to="/support">Kontakt</Link>
                        <Link to="/impressum">Impressum</Link>
                        <Link to="/datenschutz">Datenschutz</Link>
                        <Link to="/agb">AGB</Link>
                        <Link to="/content-guidelines">Content &amp; Community Guidelines</Link>
                    </nav>
                    <p className="fetivo-footer__legal">
                        Diese Webseite darf ausschlie&szlig;lich von Personen genutzt werden, die mindestens 18 Jahre alt sind. Die Plattform stellt
                        einen virtuellen Chat-Dienst f&uuml;r unterhaltsame, flirtende und fantasievolle Gespr&auml;che zur Verf&uuml;gung. Hierbei
                        werden fiktive Profile verwendet, mit denen keine pers&ouml;nlichen oder realen Treffen m&ouml;glich sind. Die Kosten je
                        Nachricht k&ouml;nnen je nach gew&auml;hlter Zahlungsart unterschiedlich ausfallen. Bitte informieren Sie sich vor der Nutzung
                        der Webseite &uuml;ber die geltenden Nutzungsbedingungen und Datenschutzbestimmungen.
                    </p>
                </footer>

                <LoginModal open={loginModalOpen} onClose={() => setLoginModalOpen(false)} />
                <ForgotPasswordModal open={isForgotPasswordOpen} />
                {isSetPasswordOpen && <ForgotPasswordModalx />}
            </main>
        </ThemeProvider>
    );
});
